import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function ModelsContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">模型</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 支持多种 AI 模型，提供模型选择向导、配置键、CLI 命令和模型注册表等功能。
        </p>
      </div>

      <SectionTitle id="model-how">模型选择工作原理</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 按以下优先级选择模型：
      </p>
      <div className="space-y-2 my-4">
        {[
          '会话级别覆盖（/model 命令临时切换）',
          '智能体配置中的 model 字段',
          '全局默认模型配置',
          '内置默认模型（claude-3-5-sonnet）',
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0">{i + 1}</span>
            <span className="text-slate-300 text-sm">{item}</span>
          </div>
        ))}
      </div>

      <SectionTitle id="model-recommend">快速模型推荐</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
        {[
          { name: 'claude-3-5-sonnet-20241022', tag: '推荐', desc: '综合能力最强，适合大多数场景', color: 'emerald' },
          { name: 'claude-3-5-haiku-20241022', tag: '快速', desc: '响应速度快，成本低，适合简单任务', color: 'cyan' },
          { name: 'gpt-4o', tag: '多模态', desc: '支持图像理解，OpenAI 旗舰模型', color: 'amber' },
          { name: 'gemini-2.0-flash', tag: '免费', desc: 'Google 免费模型，适合测试', color: 'teal' },
        ].map((m) => (
          <div key={m.name} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <code className="text-cyan-400 font-mono text-xs">{m.name}</code>
              <span className={`text-xs px-2 py-0.5 rounded-full bg-${m.color}-500/10 text-${m.color}-400 border border-${m.color}-500/20`}>{m.tag}</span>
            </div>
            <p className="text-xs text-slate-400">{m.desc}</p>
          </div>
        ))}
      </div>

      <SectionTitle id="model-wizard">设置向导</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        使用设置向导快速配置模型：
      </p>
      <CodeBlock language="bash" code={`# 启动交互式设置向导
openclaw setup

# 向导会引导你：
# 1. 选择 AI 提供商（Anthropic / OpenAI / Google / OpenRouter / 本地）
# 2. 输入 API 密钥
# 3. 选择默认模型
# 4. 测试连接`} />

      <SectionTitle id="model-config-keys">配置键（概述）</SectionTitle>
      <CodeBlock language="json5" code={`{
  // 全局模型配置
  models: {
    // 默认模型
    default: "claude-3-5-sonnet-20241022",
    
    // Anthropic 配置
    anthropic: {
      apiKey: "YOUR_ANTHROPIC_API_KEY",
    },
    
    // OpenAI 配置
    openai: {
      apiKey: "YOUR_OPENAI_API_KEY",
      baseUrl: "https://api.openai.com/v1",  // 可自定义
    },
    
    // Google 配置
    google: {
      apiKey: "YOUR_GOOGLE_API_KEY",
    },
    
    // OpenRouter 配置
    openrouter: {
      apiKey: "YOUR_OPENROUTER_API_KEY",
    },
    
    // 本地模型（Ollama）
    ollama: {
      baseUrl: "http://localhost:11434",
    }
  }
}`} />

      <SectionTitle id="model-not-allowed">Model is not allowed</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        如果遇到 "Model is not allowed" 错误，可能的原因：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>API 密钥没有访问该模型的权限（如 Claude 需要 Tier 1+ 账户才能使用 Opus）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>模型名称拼写错误，使用 <code className="text-amber-400 bg-slate-800/60 px-1 rounded text-xs">openclaw models list</code> 查看可用模型</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-error-warning-line text-amber-400 mt-0.5 flex-shrink-0"></i><span>该模型在你的地区不可用</span></li>
      </ul>
      <InfoBox type="tip">
        使用 OpenRouter 可以访问几乎所有主流模型，且支持按需付费，无需分别申请各家 API 密钥。
      </InfoBox>

      <SectionTitle id="model-switch">在聊天中切换模型（/model）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        在聊天中使用 /model 命令临时切换模型（仅对当前会话生效）：
      </p>
      <CodeBlock language="bash" code={`# 切换到指定模型
/model claude-3-opus-20240229

# 查看当前使用的模型
/model

# 重置为默认模型
/model reset`} />

      <SectionTitle id="model-cli">CLI 命令</SectionTitle>
      <CodeBlock language="bash" code={`# 列出所有可用模型
openclaw models list

# 列出特定提供商的模型
openclaw models list --provider anthropic

# 测试模型连接
openclaw models test --model claude-3-5-sonnet-20241022

# 查看模型详情
openclaw models info --model claude-3-5-sonnet-20241022`} />

      <SectionTitle id="model-scan">扫描（OpenRouter 免费模型）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        扫描 OpenRouter 上的免费模型：
      </p>
      <CodeBlock language="bash" code={`# 扫描 OpenRouter 免费模型
openclaw models scan --provider openrouter --free-only

# 输出示例：
# google/gemini-2.0-flash-exp:free  - 免费，128K 上下文
# meta-llama/llama-3.1-8b-instruct:free  - 免费，128K 上下文
# mistralai/mistral-7b-instruct:free  - 免费，32K 上下文`} />

      <SectionTitle id="model-registry">模型注册表（models.json）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 维护一个本地模型注册表，记录所有已知模型的参数：
      </p>
      <CodeBlock language="json5" code={`// ~/.openclaw/models.json（示例）
{
  "claude-3-5-sonnet-20241022": {
    provider: "anthropic",
    contextWindow: 200000,
    maxOutputTokens: 8192,
    supportsVision: true,
    supportsToolUse: true,
    inputCostPer1M: 3.0,   // USD
    outputCostPer1M: 15.0, // USD
  },
  "gpt-4o": {
    provider: "openai",
    contextWindow: 128000,
    maxOutputTokens: 16384,
    supportsVision: true,
    supportsToolUse: true,
    inputCostPer1M: 2.5,
    outputCostPer1M: 10.0,
  }
}`} />
      <InfoBox type="info">
        模型注册表会随 OpenClaw 更新自动同步。你也可以手动编辑 models.json 添加自定义模型或修改参数。
      </InfoBox>
    </div>
  );
}
